import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, IconButton, Collapse, Stack, Chip, Alert,
  List, ListItem, ListItemIcon, ListItemText, Divider, Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Refresh as RefreshIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { ValidationSummary, useFlowValidation, VALIDATION_TYPES } from './FlowValidator';

function ValidationPanel({
  nodes = [],
  edges = [],
  globalState = {},
  onNodeClick = () => {},
  onHighlightNodes = () => {},
  collapsed = false,
  onToggleCollapse = () => {},
}) {
  const [showErrors, setShowErrors] = useState(true);
  const [showWarnings, setShowWarnings] = useState(true);
  const [showInfos, setShowInfos] = useState(false);
  const [showSuccesses, setShowSuccesses] = useState(false);

  // Get validation results
  const validationResults = useFlowValidation(nodes, edges, globalState);

  // Filter results based on visibility settings
  const filteredResults = validationResults.filter((result) => {
    switch (result.type) {
      case VALIDATION_TYPES.ERROR: return showErrors;
      case VALIDATION_TYPES.WARNING: return showWarnings;
      case VALIDATION_TYPES.INFO: return showInfos;
      case VALIDATION_TYPES.SUCCESS: return showSuccesses;
      default: return true;
    }
  });

  // Calculate summary
  const summary = {
    total: validationResults.length,
    errors: validationResults.filter((r) => r.type === VALIDATION_TYPES.ERROR).length,
    warnings: validationResults.filter((r) => r.type === VALIDATION_TYPES.WARNING).length,
    infos: validationResults.filter((r) => r.type === VALIDATION_TYPES.INFO).length,
    successes: validationResults.filter((r) => r.type === VALIDATION_TYPES.SUCCESS).length,
  };

  const hasIssues = summary.errors > 0 || summary.warnings > 0;

  const handleNodeHighlight = useCallback((result) => {
    if (result.data?.nodes && result.data.nodes.length > 0) {
      onHighlightNodes(result.data.nodes);
    }
  }, [onHighlightNodes]);

  const getStatusColor = () => {
    if (summary.errors > 0) return 'error';
    if (summary.warnings > 0) return 'warning';
    if (summary.successes > 0) return 'success';
    return 'info';
  };

  const getStatusIcon = () => {
    if (summary.errors > 0) return <ErrorIcon />;
    if (summary.warnings > 0) return <WarningIcon />;
    if (summary.successes > 0) return <SuccessIcon />;
    return <InfoIcon />;
  };

  return (
    <Paper sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: hasIssues ? 2 : 1,
      borderColor: hasIssues ? `${getStatusColor()}.main` : 'divider',
    }}
    >
      {/* Header */}
      <Box sx={{
        p: 2,
        bgcolor: hasIssues ? `${getStatusColor()}.lighter` : 'background.default',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getStatusIcon()}
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Flow Validation
          </Typography>
          <Chip
            label={summary.total}
            size="small"
            color={getStatusColor()}
            variant={hasIssues ? 'filled' : 'outlined'}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Refresh validation">
            <IconButton size="small" onClick={() => window.location.reload()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onToggleCollapse}>
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Collapse in={!collapsed}>
        <Box sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
          {/* Quick Status */}
          {hasIssues && (
            <Alert
              severity={summary.errors > 0 ? 'error' : 'warning'}
              sx={{ m: 2, mb: 1 }}
              action={(
                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<ErrorIcon />}
                    label={summary.errors}
                    size="small"
                    color="error"
                    variant={showErrors ? 'filled' : 'outlined'}
                    onClick={() => setShowErrors(!showErrors)}
                  />
                  <Chip
                    icon={<WarningIcon />}
                    label={summary.warnings}
                    size="small"
                    color="warning"
                    variant={showWarnings ? 'filled' : 'outlined'}
                    onClick={() => setShowWarnings(!showWarnings)}
                  />
                </Stack>
              )}
            >
              {summary.errors > 0
                ? `${summary.errors} error(s) need attention`
                : `${summary.warnings} warning(s) to review`}
            </Alert>
          )}

          {/* Filter Controls */}
          <Box sx={{ p: 2, pt: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                icon={showErrors ? <ShowIcon /> : <HideIcon />}
                label={`${summary.errors} Errors`}
                size="small"
                color="error"
                variant={showErrors ? 'filled' : 'outlined'}
                onClick={() => setShowErrors(!showErrors)}
                disabled={summary.errors === 0}
              />
              <Chip
                icon={showWarnings ? <ShowIcon /> : <HideIcon />}
                label={`${summary.warnings} Warnings`}
                size="small"
                color="warning"
                variant={showWarnings ? 'filled' : 'outlined'}
                onClick={() => setShowWarnings(!showWarnings)}
                disabled={summary.warnings === 0}
              />
              <Chip
                icon={showInfos ? <ShowIcon /> : <HideIcon />}
                label={`${summary.infos} Tips`}
                size="small"
                color="info"
                variant={showInfos ? 'filled' : 'outlined'}
                onClick={() => setShowInfos(!showInfos)}
                disabled={summary.infos === 0}
              />
              <Chip
                icon={showSuccesses ? <ShowIcon /> : <HideIcon />}
                label={`${summary.successes} Good`}
                size="small"
                color="success"
                variant={showSuccesses ? 'filled' : 'outlined'}
                onClick={() => setShowSuccesses(!showSuccesses)}
                disabled={summary.successes === 0}
              />
            </Stack>
          </Box>

          {/* Validation Results */}
          {filteredResults.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {validationResults.length === 0
                  ? 'No validation results available'
                  : 'No results match current filters'}
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ px: 2, pb: 2 }}>
              {filteredResults.map((result, index) => (
                <React.Fragment key={result.id || index}>
                  <ListItem
                    sx={{
                      borderLeft: 3,
                      borderLeftColor: result.isValid ? 'success.main' : `${result.type}.main`,
                      mb: 1,
                      bgcolor: result.isValid ? 'success.lighter' : 'error.lighter',
                      borderRadius: 1,
                      cursor: result.data?.nodes?.length > 0 ? 'pointer' : 'default',
                      '&:hover': result.data?.nodes?.length > 0 ? {
                        bgcolor: result.isValid ? 'success.light' : 'error.light',
                      } : {},
                    }}
                    onClick={() => handleNodeHighlight(result)}
                  >
                    <ListItemIcon>
                      {result.isValid ? (
                        <SuccessIcon color="success" />
                      ) : (
                        <>
                          {result.type === VALIDATION_TYPES.ERROR && <ErrorIcon color="error" />}
                          {result.type === VALIDATION_TYPES.WARNING && <WarningIcon color="warning" />}
                          {result.type === VALIDATION_TYPES.INFO && <InfoIcon color="info" />}
                          {result.type === VALIDATION_TYPES.SUCCESS && <SuccessIcon color="success" />}
                        </>
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.message}
                      secondary={result.details}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: result.isValid ? 400 : 600,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                    {result.data?.nodes && result.data.nodes.length > 0 && (
                      <Chip
                        label={`${result.data.nodes.length}`}
                        size="small"
                        variant="outlined"
                        color={result.type}
                      />
                    )}
                  </ListItem>
                  {index < filteredResults.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Help Text */}
          <Box sx={{ p: 2, pt: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              💡 Click on validation items to highlight related nodes in the flow
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              🔧 Fix errors and warnings to improve your flow's reliability
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default ValidationPanel;
